/**
 * Página de Gestión de Parámetros del Sistema
 * Permite crear, editar y eliminar parámetros y sus valores
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { parametrosAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  Settings,
  Plus,
  Search,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  Save,
  X,
  ListOrdered,
  Type,
  Loader2,
  AlertCircle,
  Info,
  Filter,
  RefreshCw,
  Copy,
  Undo2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';

const Parametros = () => {
  const { user } = useAuth();
  
  // Estados principales
  const [parametros, setParametros] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [grupoFilter, setGrupoFilter] = useState('todos');
  
  // Estado de grupos expandidos
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Dialog de crear/editar parámetro
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParametro, setEditingParametro] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    grupo: '',
    descripcion: '',
    tipo: 'simple',
    editable: true,
    valores: [{ valor: '', etiqueta: '', orden: 0 }]
  });
  
  // Dialog de confirmación de eliminación
  const [deleteDialog, setDeleteDialog] = useState({ open: false, parametro: null });
  
  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async (isRefresh = false) => {
    // Si es refresh, usar estado sutil; si es carga inicial, usar loading completo
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const [parametrosRes, gruposRes] = await Promise.all([
        parametrosAPI.getAll(),
        parametrosAPI.getGrupos()
      ]);
      setParametros(parametrosRes.data || []);
      setGrupos(gruposRes.data || []);
      
      // Expandir todos los grupos por defecto solo en carga inicial
      if (!isRefresh) {
        const expanded = {};
        (gruposRes.data || []).forEach(g => expanded[g] = true);
        setExpandedGroups(expanded);
      }
    } catch (error) {
      toast.error('Error al cargar parámetros');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Refresh sutil
  const handleRefresh = () => {
    loadData(true);
  };
  
  // Filtrar y agrupar parámetros
  const parametrosFiltrados = useMemo(() => {
    let filtered = parametros;
    
    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.codigo.toLowerCase().includes(term) ||
        p.nombre.toLowerCase().includes(term) ||
        p.grupo.toLowerCase().includes(term) ||
        (p.descripcion && p.descripcion.toLowerCase().includes(term))
      );
    }
    
    // Filtrar por grupo
    if (grupoFilter !== 'todos') {
      filtered = filtered.filter(p => p.grupo === grupoFilter);
    }
    
    return filtered;
  }, [parametros, searchTerm, grupoFilter]);
  
  // Agrupar parámetros
  const parametrosAgrupados = useMemo(() => {
    const grouped = {};
    parametrosFiltrados.forEach(p => {
      if (!grouped[p.grupo]) {
        grouped[p.grupo] = [];
      }
      grouped[p.grupo].push(p);
    });
    return grouped;
  }, [parametrosFiltrados]);
  
  // Toggle grupo expandido
  const toggleGroup = (grupo) => {
    setExpandedGroups(prev => ({
      ...prev,
      [grupo]: !prev[grupo]
    }));
  };
  
  // Abrir dialog para crear
  const handleCreate = () => {
    setEditingParametro(null);
    setFormData({
      codigo: '',
      nombre: '',
      grupo: '',
      descripcion: '',
      tipo: 'simple',
      editable: true,
      valores: [{ valor: '', etiqueta: '', orden: 0 }]
    });
    setDialogOpen(true);
  };
  
  // Abrir dialog para editar
  const handleEdit = (parametro) => {
    setEditingParametro(parametro);
    setFormData({
      codigo: parametro.codigo,
      nombre: parametro.nombre,
      grupo: parametro.grupo,
      descripcion: parametro.descripcion || '',
      tipo: parametro.tipo || 'simple',
      editable: parametro.editable !== false,
      valores: parametro.valores?.length > 0 
        ? parametro.valores.map(v => ({
            id: v.id,
            valor: v.valor,
            etiqueta: v.etiqueta || '',
            orden: v.orden || 0
          }))
        : [{ valor: '', etiqueta: '', orden: 0 }]
    });
    setDialogOpen(true);
  };
  
  // Abrir dialog para clonar (nuevo parámetro con datos pre-llenados)
  const handleClone = (parametro) => {
    setEditingParametro(null); // Es un nuevo parámetro, no edición
    setFormData({
      codigo: `${parametro.codigo}_copia`, // Sugerir un código diferente
      nombre: `${parametro.nombre} (Copia)`,
      grupo: parametro.grupo,
      descripcion: parametro.descripcion || '',
      tipo: parametro.tipo || 'simple',
      editable: parametro.editable !== false,
      valores: parametro.valores?.length > 0 
        ? parametro.valores.map((v, idx) => ({
            // Sin ID para que se creen como nuevos
            valor: v.valor,
            etiqueta: v.etiqueta || '',
            orden: v.orden || idx
          }))
        : [{ valor: '', etiqueta: '', orden: 0 }]
    });
    setDialogOpen(true);
    toast.info('Modifica el código y nombre antes de guardar');
  };
  
  // Guardar parámetro
  const handleSave = async () => {
    // Validaciones
    if (!formData.codigo.trim()) {
      toast.error('El código es requerido');
      return;
    }
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.grupo.trim()) {
      toast.error('El grupo es requerido');
      return;
    }
    
    // Validar que al menos tenga un valor NO marcado como pendingDelete
    const valoresValidos = formData.valores.filter(
      v => !v.pendingDelete && v.valor.trim()
    );
    if (valoresValidos.length === 0) {
      toast.error('Debe conservar al menos un valor');
      return;
    }
    
    setSaving(true);
    try {
      if (editingParametro) {
        // Actualizar parámetro existente
        await parametrosAPI.update(editingParametro.codigo, {
          nombre: formData.nombre,
          grupo: formData.grupo,
          descripcion: formData.descripcion,
          tipo: formData.tipo,
          editable: formData.editable
        });

        // 1) Eliminar los valores marcados como pendingDelete (soft-check en backend).
        //    Si CUALQUIER eliminación falla (409 por dependencia u otro error),
        //    se aborta el guardado y se muestra el detail del backend en el toast.
        const aEliminar = formData.valores.filter(v => v.pendingDelete && v.id);
        for (const valor of aEliminar) {
          try {
            await parametrosAPI.deleteValor(valor.id);
          } catch (err) {
            const detail = err.response?.data?.detail
              || `No se pudo eliminar el valor "${valor.valor}"`;
            toast.error(detail);
            setSaving(false);
            return;
          }
        }

        // 2) Actualizar valores existentes y crear nuevos (ignora pendingDelete)
        for (const valor of formData.valores) {
          if (valor.pendingDelete) continue;
          if (valor.id) {
            // Actualizar valor existente
            await parametrosAPI.updateValor(valor.id, {
              valor: valor.valor,
              etiqueta: valor.etiqueta,
              orden: valor.orden
            });
          } else if (valor.valor.trim()) {
            // Crear nuevo valor
            await parametrosAPI.addValor(editingParametro.codigo, {
              valor: valor.valor,
              etiqueta: valor.etiqueta,
              orden: valor.orden
            });
          }
        }

        toast.success('Parámetro actualizado correctamente');
      } else {
        // Crear nuevo parámetro
        await parametrosAPI.create({
          codigo: formData.codigo,
          nombre: formData.nombre,
          grupo: formData.grupo,
          descripcion: formData.descripcion,
          tipo: formData.tipo,
          editable: formData.editable,
          valores: valoresValidos.map((v, idx) => ({
            valor: v.valor,
            etiqueta: v.etiqueta,
            orden: v.orden || idx
          }))
        });
        toast.success('Parámetro creado correctamente');
      }
      
      setDialogOpen(false);
      loadData(true); // Refresh sutil
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar parámetro');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  // Confirmar eliminación
  const handleDeleteConfirm = (parametro) => {
    setDeleteDialog({ open: true, parametro });
  };
  
  // Eliminar parámetro
  const handleDelete = async () => {
    const { parametro } = deleteDialog;
    if (!parametro) return;
    
    try {
      await parametrosAPI.delete(parametro.codigo);
      toast.success('Parámetro eliminado correctamente');
      setDeleteDialog({ open: false, parametro: null });
      loadData(true); // Refresh sutil
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar parámetro');
      console.error(error);
    }
  };
  
  // Agregar valor al formulario
  const addValorField = () => {
    setFormData(prev => ({
      ...prev,
      valores: [...prev.valores, { valor: '', etiqueta: '', orden: prev.valores.length }]
    }));
  };
  
  // Eliminar valor del formulario.
  // - Si el valor tiene `id` (existe en la BD): se MARCA como pendingDelete y
  //   se conserva en el array para que el usuario pueda restaurarlo. La
  //   eliminación real ocurre al guardar (llamando a parametrosAPI.deleteValor).
  // - Si el valor es nuevo (sin id): se filtra del array directamente.
  const removeValorField = (index) => {
    const target = formData.valores[index];
    if (!target) return;
    // Contar cuántos valores quedarían "visibles" (no pendingDelete) tras esta acción
    const activos = formData.valores.filter((v, i) => i !== index && !v.pendingDelete);
    if (activos.length < 1) {
      toast.error('El parámetro debe conservar al menos un valor');
      return;
    }
    if (target.id) {
      // Marca como pending-delete (se elimina al guardar)
      setFormData(prev => ({
        ...prev,
        valores: prev.valores.map((v, i) =>
          i === index ? { ...v, pendingDelete: true } : v
        ),
      }));
      toast.info(`Valor "${target.valor}" marcado para eliminación. Guarda para confirmar.`);
    } else {
      // Nunca se persistió: se puede quitar directamente
      setFormData(prev => ({
        ...prev,
        valores: prev.valores.filter((_, i) => i !== index),
      }));
    }
  };

  // Restaurar un valor previamente marcado para eliminación
  const restoreValorField = (index) => {
    setFormData(prev => ({
      ...prev,
      valores: prev.valores.map((v, i) =>
        i === index ? { ...v, pendingDelete: false } : v
      ),
    }));
  };
  
  // Actualizar valor en el formulario
  const updateValorField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      valores: prev.valores.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }
  
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#333333] flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#9d8272]" />
            Parámetros del Sistema
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            Configura los parámetros generales de la plataforma
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#9d8272] hover:bg-[#8c7161] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Parámetro
        </Button>
      </div>
      
      {/* Filtros */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
              <Input
                placeholder="Buscar por código, nombre o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#c5b8a2]"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select value={grupoFilter} onValueChange={setGrupoFilter}>
                <SelectTrigger className="border-[#c5b8a2]">
                  <Filter className="w-4 h-4 mr-2 text-[#888888]" />
                  <SelectValue placeholder="Filtrar por grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los grupos</SelectItem>
                  {grupos.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-[#c5b8a2]"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Lista de parámetros agrupados con overlay de refresh */}
      <div className="relative">
        {/* Overlay sutil de refresh */}
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Loader2 className="w-5 h-5 animate-spin text-[#9d8272]" />
              <span className="text-sm text-[#666666]">Actualizando...</span>
            </div>
          </div>
        )}
        
        {Object.keys(parametrosAgrupados).length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Info className="w-12 h-12 text-[#c5b8a2] mx-auto mb-4" />
            <p className="text-[#888888]">
              {searchTerm || grupoFilter !== 'todos'
                ? 'No se encontraron parámetros con los filtros aplicados'
                : 'No hay parámetros configurados'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(parametrosAgrupados).sort().map(([grupo, params]) => (
            <Card key={grupo} className="overflow-hidden">
              {/* Header del grupo */}
              <CardHeader
                className="cursor-pointer bg-[#f8f6f4] hover:bg-[#f0ece8] transition-colors py-3"
                onClick={() => toggleGroup(grupo)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedGroups[grupo] ? (
                      <ChevronDown className="w-5 h-5 text-[#9d8272]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#9d8272]" />
                    )}
                    <CardTitle className="text-base font-semibold text-[#333333]">
                      {grupo}
                    </CardTitle>
                    <Badge variant="secondary" className="bg-[#c5b8a2]/20 text-[#666666]">
                      {params.length} {params.length === 1 ? 'parámetro' : 'parámetros'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              {/* Contenido del grupo */}
              {expandedGroups[grupo] && (
                <CardContent className="p-0">
                  <div className="divide-y divide-[#e5e0da]">
                    {params.map(param => (
                      <div
                        key={param.codigo}
                        className="p-4 hover:bg-[#faf9f7] transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          {/* Info del parámetro */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="text-sm font-mono bg-[#f0ece8] px-2 py-0.5 rounded text-[#9d8272]">
                                {param.codigo}
                              </code>
                              <Badge 
                                variant="outline"
                                className={`text-xs ${
                                  param.tipo === 'lista' 
                                    ? 'border-blue-300 text-blue-700 bg-blue-50' 
                                    : 'border-green-300 text-green-700 bg-green-50'
                                }`}
                              >
                                {param.tipo === 'lista' ? (
                                  <><ListOrdered className="w-3 h-3 mr-1" /> Lista</>
                                ) : (
                                  <><Type className="w-3 h-3 mr-1" /> Simple</>
                                )}
                              </Badge>
                              {!param.editable && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                                  No editable
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium text-[#333333] mt-1">
                              {param.nombre}
                            </p>
                            {param.descripcion && (
                              <p className="text-xs text-[#888888] mt-0.5">
                                {param.descripcion}
                              </p>
                            )}
                            
                            {/* Valores */}
                            <div className="mt-2 space-y-1">
                              {param.valores?.map((val, idx) => (
                                <div key={val.id || idx} className="flex items-center gap-2 text-sm">
                                  <span className="text-[#888888]">→</span>
                                  <span className="font-mono text-xs bg-[#f5f3f0] px-2 py-0.5 rounded truncate max-w-[300px]">
                                    {val.valor || '(vacío)'}
                                  </span>
                                  {val.etiqueta && (
                                    <span className="text-xs text-[#888888]">
                                      ({val.etiqueta})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Acciones */}
                          {/* Acciones */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClone(param)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              title="Clonar parámetro"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(param)}
                              disabled={!param.editable}
                              className="text-[#9d8272] hover:text-[#8c7161] hover:bg-[#f0ece8]"
                              title="Editar parámetro"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteConfirm(param)}
                              disabled={!param.editable}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Eliminar parámetro"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
      </div>
      
      {/* Dialog de Crear/Editar Parámetro */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#9d8272]" />
              {editingParametro ? 'Editar Parámetro' : 'Nuevo Parámetro'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Campos del parámetro */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">
                  Código <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  placeholder="ej: nps_url_token"
                  className="border-[#c5b8a2] font-mono"
                  disabled={!!editingParametro}
                />
                <p className="text-xs text-[#888888] mt-1">
                  Identificador único (sin espacios)
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">
                  Nombre <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="ej: URL Token (OAuth)"
                  className="border-[#c5b8a2]"
                />
                <p className="text-xs text-[#888888] mt-1">
                  Etiqueta visible en la UI
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">
                  Grupo <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.grupo}
                  onChange={(e) => setFormData(prev => ({ ...prev, grupo: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  placeholder="ej: wa_nps"
                  className="border-[#c5b8a2]"
                  list="grupos-list"
                />
                <datalist id="grupos-list">
                  {grupos.map(g => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
                <p className="text-xs text-[#888888] mt-1">
                  Agrupación de parámetros relacionados
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Tipo</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger className="border-[#c5b8a2]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">
                      <span className="flex items-center gap-2">
                        <Type className="w-4 h-4" /> Simple (1 valor)
                      </span>
                    </SelectItem>
                    <SelectItem value="lista">
                      <span className="flex items-center gap-2">
                        <ListOrdered className="w-4 h-4" /> Lista (N valores)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción del propósito de este parámetro..."
                className="border-[#c5b8a2] resize-none"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.editable}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, editable: checked }))}
              />
              <Label className="text-sm">Editable por administradores</Label>
            </div>
            
            {/* Sección de Valores */}
            <div className="border-t border-[#e5e0da] pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">
                  Valores <span className="text-red-500">*</span>
                </Label>
                {formData.tipo === 'lista' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addValorField}
                    className="border-[#c5b8a2] text-[#9d8272]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar valor
                  </Button>
                )}
              </div>
              
              <div className="space-y-3">
                {formData.valores.map((val, idx) => {
                  const isPending = val.pendingDelete === true;
                  return (
                  <div 
                    key={idx}
                    className={`flex items-start gap-2 p-3 rounded-lg ${
                      isPending
                        ? 'bg-red-50 border border-dashed border-red-300'
                        : 'bg-[#faf9f7]'
                    }`}
                    data-testid={`param-valor-row-${idx}`}
                  >
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-2">
                        <Input
                          value={val.valor}
                          onChange={(e) => updateValorField(idx, 'valor', e.target.value)}
                          placeholder="Valor"
                          disabled={isPending}
                          className={`border-[#c5b8a2] text-sm ${
                            isPending ? 'line-through text-red-500 opacity-70' : ''
                          }`}
                          data-testid={`param-valor-input-${idx}`}
                        />
                        {isPending && (
                          <div className="text-[11px] text-red-600 mt-1 italic">
                            Marcado para eliminación · se aplicará al guardar
                          </div>
                        )}
                      </div>
                      {formData.tipo === 'lista' && (
                        <>
                          <div>
                            <Input
                              value={val.etiqueta}
                              onChange={(e) => updateValorField(idx, 'etiqueta', e.target.value)}
                              placeholder="Etiqueta (opcional)"
                              disabled={isPending}
                              className={`border-[#c5b8a2] text-sm ${
                                isPending ? 'line-through text-red-500 opacity-70' : ''
                              }`}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {formData.tipo === 'lista' && formData.valores.length > 1 && (
                      isPending ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => restoreValorField(idx)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          data-testid={`param-valor-restore-${idx}`}
                          title="Restaurar (deshacer eliminación)"
                        >
                          <Undo2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeValorField(idx)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`param-valor-delete-${idx}`}
                          title="Eliminar valor"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#c5b8a2]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#9d8272] hover:bg-[#8c7161] text-white"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Guardar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, parametro: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Eliminar Parámetro
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar el parámetro <strong>{deleteDialog.parametro?.codigo}</strong>?
              <br />
              Esta acción eliminará también todos sus valores asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#c5b8a2]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Parametros;
